import GeneralDialog from '../../../../../shared/general-dialog.tsx';
import { useLabelsQuery } from '../../gpanel-contacts-queries.ts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useStore } from '../../../../state/zustand-store.ts';
import SharedAutocomplete from '../../../../../shared/shared-autocomplete';
import {
  GPanelContactsImportRequest,
  Label,
  LabelGroupsResponse,
} from '../../shared/types.ts';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useState, useMemo } from 'react';
import {
  getImportContacts,
  GPanelContactsKeys,
  importContacts,
} from '../../gpanel-contacts-api.ts';
import { Messages } from '../../../../../utils/message-constants.ts';
import { useAnalytics } from '../../../../../shared/analytics/create-events.ts';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../../shared/analytics/module-list.ts';
import ChangeLogDialog from '../../../../../shared/change-log-dialog.tsx';
import {
  GPanelContactsPermissions,
  isAbleToSeeUIElement,
} from '../../../../../utils/roles/client-roles.ts';
import { useUserSettings } from '../../../../../utils/use-user-settings.tsx';
import {
  GroupHeader,
  GroupItems,
} from '../../../../../shared/autocomplete-groups-ui';
import { GPanelContactsPagesConfig } from '../../shared/pages-config.ts';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';

interface GPanelContactsImportFormData {
  userEmail: string;
  sourceLabel: string;
  targetLabel: number | string;
  sendNotification: boolean;
  newLabelName: string;
}

interface GPanelContactsImportDialogProps {
  open: boolean;
  handleClose: () => void;
}

export default function GPanelContactsImportDialog({
  open,
  handleClose,
}: GPanelContactsImportDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { labelId } = useParams<{ labelId: string | undefined }>();
  const { data: labels = [] } = useLabelsQuery();
  const { setSnackbar } = useStore();
  const defaultTargetLabel = labels.find(
    (label) => label.id === Number(labelId)
  );
  const [isCreateNewLabel, setIsCreateNewLabel] = useState(false);
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { changeLogRoles } = useUserSettings();

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
    getValues,
    clearErrors,
    watch,
  } = useForm<GPanelContactsImportFormData>({
    values: {
      userEmail: '',
      sourceLabel: '',
      targetLabel: defaultTargetLabel ? defaultTargetLabel.id : '',
      newLabelName: '',
      sendNotification: true,
    },
  });

  const selectedUser = watch('userEmail') || '';

  const { data: sourceLabels = [], isLoading: isSourceLabelsLoading } =
    useQuery({
      queryKey: [GPanelContactsKeys.GpanelContactImportContacts, selectedUser],
      queryFn: () => (selectedUser ? getImportContacts(selectedUser) : []),
      enabled: !!selectedUser,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    });

  const { mutate, isPending } = useMutation({
    mutationFn: (request: GPanelContactsImportRequest) =>
      importContacts(
        request.userEmail,
        request.sourceLabel,
        request.targetLabel,
        request.sendNotification,
        request.newLabelName,
        changeLogMessage
      ),
    onSuccess: (response, requestData) => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: Messages.Success.Started('Importing of gPanel contacts'),
      });

      // Navigate based on whether we have a new label or not
      if (response) {
        // Update the simple labels cache
        queryClient.setQueryData<Label[]>(
          [GPanelContactsKeys.GPanelLabels],
          (oldData) => {
            const newData = oldData ? [...oldData, response] : [response];
            return newData;
          }
        );

        // Update the label groups cache (used by the sidebar)
        queryClient.setQueryData<LabelGroupsResponse>(
          [GPanelContactsKeys.LabelGroups],
          (oldData) => {
            if (oldData?.labelGroups) {
              const updatedLabelGroups = oldData.labelGroups.map((group) => ({
                ...group,
                labels: [...(group.labels || []), response],
              }));

              const newData = {
                ...oldData,
                labelGroups: updatedLabelGroups,
              };
              return newData;
            }
            return oldData;
          }
        );

        navigate(
          `/gpanel-contacts/${GPanelContactsPagesConfig.OrganizationLabels.link}/${response.id}`
        );
      } else {
        // Redirect to directory if no label was returned and target label is the directory
        if (!requestData.targetLabel) {
          navigate(
            `/gpanel-contacts/${GPanelContactsPagesConfig.OrganizationDirectory.link}`
          );
        }
      }
      handleClose();
      reset();
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        Messages.Errors.Network.Starting('importing of gPanel contacts');

      setSnackbar({
        open: true,
        severity: 'error',
        message: errorMessage,
      });
    },
    onSettled: () => {
      createEvent(analytics.directory.gpanel_contacts.import_contacts, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.gpanelContacts,
      });
    },
  });

  const onSubmit = (data: GPanelContactsImportFormData) => {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        GPanelContactsPermissions.ImportContacts.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      const submitData: GPanelContactsImportRequest = {
        ...data,
        targetLabel: data.targetLabel === '' ? null : Number(data.targetLabel),
      };
      mutate(submitData);
    }
  };

  const sortedSourceLabels = useMemo(
    () => ({
      userLabels: sourceLabels
        .filter((g) => !g.systemGroup)
        .sort((a, b) => a.formattedName.localeCompare(b.formattedName)),
      systemLabels: sourceLabels
        .filter(
          (g) =>
            g.systemGroup &&
            (g.formattedName === 'All Contacts' ||
              g.formattedName === 'My Contacts')
        )
        .sort((a, b) => a.formattedName.localeCompare(b.formattedName)),
    }),
    [sourceLabels]
  );

  return (
    <GeneralDialog
      open={open}
      handleClose={() => {
        reset();
        setIsCreateNewLabel(false);
        handleClose();
      }}
      title={'Import Contacts'}
      maxWidth="lg"
      dialogContent={
        <Stack sx={{ px: 2 }}>
          <Typography sx={{ mb: 2 }}>
            Import contacts from the selected user's google contacts label.
            Search for a user, select a specific Google Contacts label, and
            choose whether to add them to an existing gPanel label or create a
            new one.
          </Typography>
          <Box sx={{ width: 400 }}>
            <Controller
              control={control}
              name="userEmail"
              rules={{
                required: 'User is required',
              }}
              render={({ field }) => (
                <SharedAutocomplete
                  sources={['users']}
                  autocompleteValue={field.value}
                  setAutoCompleteValue={(value) => {
                    field.onChange(value);
                    setValue('sourceLabel', '');
                  }}
                  label="Search for a user"
                  errors={!!errors.userEmail}
                  helperText={errors.userEmail?.message || ' '}
                  required
                />
              )}
            />
          </Box>
          <Controller
            control={control}
            name="sourceLabel"
            rules={{
              required: 'Source label is required',
            }}
            render={() => (
              <Autocomplete
                disabled={!selectedUser || isSourceLabelsLoading}
                options={[
                  ...sortedSourceLabels.userLabels,
                  ...sortedSourceLabels.systemLabels,
                ]}
                autoComplete
                autoHighlight
                selectOnFocus
                noOptionsText="No Labels Found"
                renderGroup={(params) => (
                  <li key={params.key}>
                    <GroupHeader>{params.group}</GroupHeader>
                    <GroupItems>{params.children}</GroupItems>
                  </li>
                )}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    key={option.resourceName}
                    style={{
                      width: '100%',
                      ...props.style,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography>{option.formattedName}</Typography>
                    <Chip
                      label={Number(option.memberCount).toLocaleString()}
                      size="small"
                    />
                  </li>
                )}
                groupBy={(option) =>
                  option.systemGroup ? 'System Labels' : 'User Labels'
                }
                getOptionLabel={(option) => option.formattedName}
                sx={{ width: 400 }}
                value={
                  sourceLabels.find(
                    (label) => label.formattedName === watch('sourceLabel')
                  ) || null
                }
                onChange={(_event, newValue) => {
                  if (!newValue) {
                    setValue('sourceLabel', '');
                    return;
                  }
                  setValue('sourceLabel', newValue.formattedName);
                  clearErrors('sourceLabel');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Source Label"
                    error={!!errors.sourceLabel}
                    helperText={errors.sourceLabel?.message || ' '}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isSourceLabelsLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
              />
            )}
          />
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Import contacts to
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              This is an optional step; if no target label is selected or
              created the contacts will be imported into the gPanel Directory.
            </Typography>
            <Controller
              control={control}
              name="targetLabel"
              render={({ field }) => (
                <TextField
                  id="target-label-select"
                  select
                  label="Target Label"
                  variant="outlined"
                  value={field.value}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value === '' ? '' : Number(value));
                    if (Number(value) !== -1 && value !== '') {
                      setValue('newLabelName', '');
                      setIsCreateNewLabel(false);
                    } else if (Number(value) === -1) {
                      setIsCreateNewLabel(true);
                    } else {
                      setValue('newLabelName', '');
                      setIsCreateNewLabel(false);
                    }
                  }}
                  sx={{ width: 400, mb: 1 }}
                >
                  <MenuItem value="">No label (import to directory)</MenuItem>
                  <MenuItem value={-1}>Create New Label</MenuItem>
                  <Divider />
                  {labels.map((label) => (
                    <MenuItem key={label.id} value={label.id}>
                      {label.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Box>

          {isCreateNewLabel && (
            <Box sx={{ mt: 1 }}>
              <Controller
                control={control}
                rules={{
                  required: 'Label name is required',
                }}
                name="newLabelName"
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="new-label-name"
                    label="Label Name"
                    variant="outlined"
                    error={!!errors.newLabelName}
                    helperText={errors.newLabelName?.message || ' '}
                    required
                    sx={{ width: 400 }}
                    autoFocus
                  />
                )}
              />
            </Box>
          )}
          <Controller
            control={control}
            name="sendNotification"
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label="Email notification on completion."
              />
            )}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              const submitData: GPanelContactsImportRequest = {
                ...getValues(),
                targetLabel:
                  getValues('targetLabel') === ''
                    ? null
                    : Number(getValues('targetLabel')),
              };
              mutate(submitData);
            }}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        loading: isPending,
        disabled: isPending || !isDirty,
        action: handleSubmit(onSubmit),
        title: 'Import',
      }}
    />
  );
}
